import { getRelativePosition } from 'chart.js/helpers';
import { METRICS_DIMENSION_OPTIONS } from '../filters/metricsFilterOptions';
import { formatUserReadableDate } from '../../utils/time';
import { roundNumberIfAppropriate } from '../../utils/scales';

export const METRIC_COLORS = [
    "#1f77b4", // Blue
    "#ff7f0e", // Orange
    "#2ca02c", // Green
    "#d62728", // Red
    "#9467bd", // Purple
    "#8c564b", // Brown
    "#e377c2", // Pink
    "#7f7f7f", // Gray
    "#bcbd22", // Green
    "#17becf", // Cyan
];

export const metricColors = (dimensionName: string, idx: number, dimensionValue: string) => {
    if (dimensionName === "Acuity") {
        // Special color ordering to match Epic
        if (dimensionValue.startsWith("1")) {
            return "#d62728"; // Red
        } else if (dimensionValue.startsWith("2")) {
            return "#ff7f0e"; // Orange
        } else if (dimensionValue.startsWith("3")) {
            return "#fccf03"; // Yellow
        } else if (dimensionValue.startsWith("4")) {
            return "#2ca02c"; // Green
        } else if (dimensionValue.startsWith("5")) {
            return "#1f77b4"; // Blue
        } else {
            return "#7f7f7f"; // Gray
        }
    } else {
        return METRIC_COLORS[idx % METRIC_COLORS.length];
    }
}

export const ZONE_MAPPING: any = {
    "ALPHA": "ALPHA",
    "ALPHA_HALLWAY": "ALPHA",
    "BRAVO": "BRAVO",
    "BRAVO_HALLWAY": "BRAVO",
    "CARDINAL": "CARDINAL",
    "CARDINAL_HALLWAY": "CARDINAL",
    "DELTA": "DELTA",
    "DELTA_HALLWAY": "DELTA",
    "EMERALD": "EMERALD",
    "EMERALD_HALLWAY": "EMERALD",
    "FOXTROT": "FOXTROT",
    "FOXTROT_HALLWAY": "FOXTROT",
    "WAITING": "WAITING",
}

export const DISPOSITION_MAPPING: any = {
    "Admit Inpt": "Admit",
    "Conditional": "Conditional",
    "Discharge": "Discharge",
    "Obs-CDU": "Admit",
    "Place in Obs": "Admit",
    "Inpatient": "Admit",
    "Transfer-Psy": "Psyc",
    "Transfer": "Transfer",
    "Send L": "Transfer",
    "TranLPCH/PEC": "Transfer",
    "Observation": "Admit",
}

let debounceUpdateTime = new Date().getTime();

export const getMetricsLineOpts = (isStacked: boolean, start: Date, end: Date, chartRef: any, hoverPosition: Date | undefined, setHoverPosition: any, setClickedTime: any, setShowConfirmation: any) => {
    const options = {
        plugins: {
            corsair: {
                horizontal: false,
                vertical: true,
                color: 'red',
                dash: [],
                width: 2,
                hoverPosition: hoverPosition,
            },
            title: {
                display: false,
            },
            legend: {
                display: false,
            },
            annotation: {
                annotations: {
                    line1: {
                        type: 'line',
                        xMin: hoverPosition,
                        xMax: hoverPosition,
                        borderColor: '#999',
                        borderDash: [5, 15],
                        borderWidth: 1,
                    },
                }
            },
            tooltip: {
                animation: false,
                callbacks: {
                    title: (tooltipItems: any) => {
                        return formatUserReadableDate(tooltipItems[0].raw.x)
                    },
                    label: (context: any) => {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += roundNumberIfAppropriate(context.parsed.y);
                        }
                        return label;
                    }
                }
            },
        },
        animation: {
            duration: 0,
        },
        hover: {
            mode: 'dataset',
            intersect: false,
        },
        onHover: (event: any, elements: any, chart: any) => {
            if (new Date().getTime() - debounceUpdateTime > 60) {
                debounceUpdateTime = new Date().getTime();
                const canvasPosition = getRelativePosition(event, chartRef.current);
                const dataX = chartRef.current.scales.x.getValueForPixel(canvasPosition.x);
                setHoverPosition(new Date(dataX));
            }
            if (event.native.target) {
                event.native.target.style.cursor = elements && elements.length > 0 ? 'pointer' : 'default';
            }
        },
        onClick: (event: any, elements: any, chart: any) => {

            const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, true);

            if (points.length) {
                const firstPoint = points[0];
                const value = chart.data.datasets[firstPoint.datasetIndex].data[firstPoint.index];
                setShowConfirmation(true);
                setClickedTime(value.x);
            }
        },
        interaction: {
            mode: 'nearest',
            intersect: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
              type: 'time',
              time: {
                unit: 'day',
                displayFormats: {
                    day: 'dd MMM DD'
                },
              },
              min: start,
              max: end,
            //   max: new Date(end.getTime() + (30 * 24 * 60 * 60 * 1000))
            },
            y: {
              stacked: isStacked,
              min: 0,
            },
        },
    };
    return options;
}

export const createDiagonalPattern = (color = 'black') => {
    let shape = document.createElement('canvas')
    shape.width = 10
    shape.height = 10
    let c: any = shape.getContext('2d');
    c.strokeStyle = color
    c.beginPath()
    c.moveTo(2, 0)
    c.lineTo(10, 8)
    c.stroke()
    c.beginPath()
    c.moveTo(0, 8)
    c.lineTo(2, 10)
    c.stroke()
    return c.createPattern(shape, 'repeat')
}

export const getMetricsBarOpts = (xStacked: boolean = true, yStacked: boolean = true) => {
    const options = {
        plugins: {
            title: {
                display: false,
            },
            legend: {
                display: false,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: xStacked,
            },
            y: {
                stacked: yStacked,
            },
        },
    };
    return options;
}

export const getMetricsTimeTrendOpts = (plotWidth: number, title: string, dimensionValues: string[]) => {
    const series = [{}];
    dimensionValues.forEach((val: string, idx: number) => {
        series.push({
            stroke: METRIC_COLORS[idx % METRIC_COLORS.length],
        })
    })
    return {
        width: plotWidth,
        height: 360,
        legend: {
            show: false
        },
        cursor: {
            y: false,
            focus: {
                prox: 5,
            },
            sync: {
                key: 'metrics',
            },
        },
        scales: {
            x: {
                time: true,
            },
            // y: {
            //     range: (self: any, dataMin: number, dataMax: number) => {return [0, dataMax + dataMax * 0.3]},
            // }
        },
        series: series,
        axes: [
            {},
            {
                // label: title,
                labelGap: 8,
            }
        ],
        plugins: [
            tooltipPlugin(title, true),
        ],
    }
}

function tooltipPlugin(title: string, isInt: boolean) {
    let seriestt: any;

    function init(u: any, opts: any, data: any) {
        let over = u.over;

        seriestt = document.createElement("div");
        seriestt.className = "u-tooltip";
        seriestt.textContent = "";
        seriestt.style.pointerEvents = "none";
        seriestt.style.position = "absolute";
        over.appendChild(seriestt);

        function hideTips() {
            seriestt.style.display = "none";
            seriestt.style.opacity = 0;
        }

        function showTips() {
            seriestt.style.display = "block";
            seriestt.style.opacity = 1;
        }

        over.addEventListener("mouseleave", () => {
            hideTips();
        });

        over.addEventListener("mouseenter", () => {
            showTips();
        });
    }

    function setCursor(u: any) {
        const {left, top, idx} = u.cursor;

        let xVal = u.data[0][idx];
        let yVal = u.data[1][idx]
        if (yVal) {
            if (isInt) {
                yVal = Math.round(yVal);
            } else {
                yVal = yVal.toFixed(2);
            }
        }

        seriestt.innerHTML = `
            <div class='u-tooltip-header'>${new Date(xVal * 1000).toLocaleString()}</div>
            <div class='u-tooltip-body'>
                ${yVal}
            </div>
        `

        seriestt.style.left = Math.round(u.valToPos(xVal, 'x') + 10) + "px";
        seriestt.style.top = Math.round(u.valToPos(yVal, 'y') + 10) + "px";
    }

    return {
        hooks: {
            init,
            setCursor,
        },
    };
}

export const getMappedDimensionValue = (filterOptionIndex: number, dim: string) => {
    if (METRICS_DIMENSION_OPTIONS[filterOptionIndex].id === "ZONE") {
        if (ZONE_MAPPING[dim]) {
            return ZONE_MAPPING[dim];
        } else {
            return "Other"
        }
    } else if (METRICS_DIMENSION_OPTIONS[filterOptionIndex].id === "DISPOSITION_STATUS") {
        if (DISPOSITION_MAPPING[dim]) {
            return DISPOSITION_MAPPING[dim];
        } else {
            return "Other"
        }
    } else {
        return dim;
    }
}
